import type { VerifyOutcome } from '@src/pages/api/braze/types';

const STORAGE_KEY = 'braze.locationVerification';

export type VerificationState = {
	deviceId: string;
	latitude: number;
	longitude: number;
	outcome: VerifyOutcome;
};

/**
 * Round to the same 4 decimals as the coordinate tolerance, so tiny jitter in
 * IP geolocation between page views does not defeat the once-per-session guard.
 */
const roundToGrid = (value: number): number => Number(value.toFixed(4));

/**
 * Outcomes worth remembering for the rest of the session.
 *
 * `timeout` and `error` are deliberately excluded: a page view cut short by a
 * navigation should get another attempt. The outcome is still written each time,
 * so a chronically failing setup costs at most one poll cycle per page view
 * rather than one per render.
 */
export const isTerminalOutcome = (outcome: VerifyOutcome): boolean =>
	outcome === 'verified' || outcome === 'mismatch' || outcome === 'not-configured';

export const readVerificationState = (): VerificationState | null => {
	try {
		// sessionStorage access itself can throw in Safari private mode.
		const raw = globalThis.sessionStorage?.getItem(STORAGE_KEY);
		if (!raw) {
			return null;
		}
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object' || typeof parsed.deviceId !== 'string') {
			return null;
		}
		return parsed as VerificationState;
	} catch {
		return null;
	}
};

export const writeVerificationState = (state: VerificationState): void => {
	try {
		globalThis.sessionStorage?.setItem(
			STORAGE_KEY,
			JSON.stringify({
				deviceId: state.deviceId,
				latitude: roundToGrid(state.latitude),
				longitude: roundToGrid(state.longitude),
				outcome: state.outcome,
			})
		);
	} catch {
		// Storage unavailable. The guard degrades to re-verifying, which the
		// server-side cache and budget already protect against.
	}
};

/**
 * True when a previous page view in this session already settled verification
 * for this exact device and location, so the whole flow can be skipped.
 */
export const hasSettledVerification = (
	stored: VerificationState | null,
	deviceId: string | undefined,
	coordinates: { latitude: number; longitude: number }
): boolean =>
	stored !== null &&
	!!deviceId &&
	stored.deviceId === deviceId &&
	stored.latitude === roundToGrid(coordinates.latitude) &&
	stored.longitude === roundToGrid(coordinates.longitude) &&
	isTerminalOutcome(stored.outcome);
